import type {ReactNode, MouseEvent} from 'react'
import {cn} from '@/lib/utils'

interface PillButtonProps {
    children: ReactNode
    selected?: boolean
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
    className?: string
    'data-testid'?: string
}

// WHY: no `transition-colors`. The auto-select effect on CertificationReviews
// (set first campaign on mount) drives unselected → selected within a single
// render commit; the 150ms colour fade put axe-core in a window where the
// computed FG was an interpolated gray on the selected bg-primary
// (≈ #dde0e4 on #cb4867 → ratio 3.4, WCAG AA fails). Snap colours on state
// flip so the contrast is always the stable selected/unselected pair.
const BASE_CLASS = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer'
const SELECTED_CLASS = 'bg-primary text-primary-foreground border-primary'
const UNSELECTED_CLASS = 'bg-background text-muted-foreground border-border hover:bg-muted'

/**
 * Compact pill-shaped filter button. Used for quick filters where radio-button
 * semantics fit better than a dropdown (e.g. campaign or status quick-pick).
 */
export function PillButton({children, selected, onClick, className, 'data-testid': testId}: PillButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={cn(BASE_CLASS, selected ? SELECTED_CLASS : UNSELECTED_CLASS, className)}
        >
            {children}
        </button>
    )
}
